import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext, UserContext } from '../../../../../context';
import { Event, EventStatus, EventType, EventUserStatus } from '../../../../../services';
import {
  eventCancelParticipation,
  eventJoinAsParticipant,
  updateCardEvent,
} from '../../../../../slices';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useFreeLoginPopup } from '../../../../hooks/useFreeLoginPopup';
import { useIsNarrowViewport } from '../../../../hooks/useIsNarrowViewport';
import { isEventPast } from '../../../../utils';
import { Button, ButtonType } from '../../../Button';
import { ConfirmationModal } from '../../../ConfirmationModal';

interface EventActionButtonProps {
  event: Event;
  className?: string;
}

export const EventActionButton: FunctionComponent<EventActionButtonProps> = memo(
  ({ event, className }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'eventItem' });

    const dispatch = useAppDispatch();

    const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

    const { id: channelId } = useContext(ChannelContext).channel;

    const { userProfile } = useContext(UserContext);

    const isNarrowViewport = useIsNarrowViewport();

    const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

    const [isJoinLoading, setIsJoinLoading] = useState(false);

    const [isCancelLoading, setIsCancelLoading] = useState(false);

    const {
      id: eventId,
      type,
      startAt,
      endAt,
      status,
      participantStatus,
      participantsCapacity,
      participantCountConfirmed,
      allowWaitlist,
      participantsRegistrationDeadlineEndAt,
      externalUrl,
      externalLabel,
    } = event;

    const isExternalUrl = type === EventType.EXTERNAL_URL;

    const isPublished = useMemo(() => status === EventStatus.PUBLISHED, [status]);

    const isAttending = useMemo(
      () => participantStatus === EventUserStatus.CONFIRMED,
      [participantStatus]
    );

    const isWaitlisted = useMemo(
      () => participantStatus === EventUserStatus.WAITLISTED,
      [participantStatus]
    );

    const isHost = useMemo(() => participantStatus === EventUserStatus.HOST, [participantStatus]);

    const isFull = useMemo(
      () => participantsCapacity > 0 && participantCountConfirmed >= participantsCapacity,
      [participantsCapacity, participantCountConfirmed]
    );

    const isPast = useMemo(() => isEventPast(startAt, endAt), [startAt, endAt]);

    const isRegistrationClosed = useMemo(
      () => new Date(participantsRegistrationDeadlineEndAt ?? startAt) <= new Date(),
      [participantsRegistrationDeadlineEndAt, startAt]
    );

    const openExternalUrl = useCallback(() => {
      if (!externalUrl) {
        return;
      }

      window.open(externalUrl, '_blank', 'noreferrer');
    }, [externalUrl]);

    const joinAsParticipant = useCallback(async () => {
      if (userProfile.isAnonymous) {
        freeLoginHandler();
        return;
      }

      setIsJoinLoading(true);

      try {
        const { payload } = await dispatch(
          eventJoinAsParticipant({ channelId, eventId, userProfile })
        ).unwrap();

        if (!payload) {
          toast.error(t('common.error-message'));
          return;
        }

        await dispatch(updateCardEvent({ channelId, eventId }));
      } catch {
        toast.error(t('common.error-message'));
      } finally {
        setIsJoinLoading(false);
      }
    }, [dispatch, channelId, eventId, freeLoginHandler, userProfile, t]);

    const onAcceptCancel = useCallback(async () => {
      setIsCancelConfirmOpen(false);
      setIsCancelLoading(true);

      try {
        await dispatch(eventCancelParticipation({ channelId, eventId, userProfile }));
        await dispatch(updateCardEvent({ channelId, eventId }));
      } catch {
        toast.error(t('common.error-message'));
      } finally {
        setIsCancelLoading(false);
      }
    }, [dispatch, channelId, eventId, userProfile, t]);

    const button = useMemo(() => {
      if (!isPublished || isPast) {
        return null;
      }

      if (isExternalUrl && externalLabel) {
        return (
          <Button
            type={ButtonType.primary}
            label={externalLabel}
            className={className}
            fullWidth={isNarrowViewport}
            onClick={openExternalUrl}
          />
        );
      }

      if (isRegistrationClosed || isHost) {
        return null;
      }

      if (isAttending || isWaitlisted) {
        return (
          <Button
            type={ButtonType.secondary}
            label={ctxT(isAttending ? 'cancel-participation' : 'leave-waitlist')}
            className={className}
            fullWidth={isNarrowViewport}
            onClick={() => setIsCancelConfirmOpen(true)}
            disabled={isCancelLoading}
            loading={isCancelLoading}
          />
        );
      }

      if (isFull && !allowWaitlist) {
        return null;
      }

      return (
        <Button
          type={ButtonType.primary}
          label={ctxT(isFull && allowWaitlist ? 'join-waitlist' : 'join')}
          className={className}
          fullWidth={isNarrowViewport}
          onClick={joinAsParticipant}
          disabled={isJoinLoading}
          loading={isJoinLoading}
        />
      );
    }, [
      allowWaitlist,
      className,
      ctxT,
      externalLabel,
      isAttending,
      isCancelLoading,
      isExternalUrl,
      isFull,
      isHost,
      isJoinLoading,
      isNarrowViewport,
      isPast,
      isPublished,
      isRegistrationClosed,
      isWaitlisted,
      joinAsParticipant,
      openExternalUrl,
    ]);

    const modalTitle = isWaitlisted ? ctxT('leave-waitlist') : ctxT('cancel-participation');

    return (
      <>
        {button}
        <ConfirmationModal
          isOpen={isCancelConfirmOpen}
          onClose={() => setIsCancelConfirmOpen(false)}
          onAccept={onAcceptCancel}
          title={modalTitle}
          subTitle={
            isWaitlisted
              ? ctxT('leave-waitlist-confirmation-sub-title')
              : ctxT('cancel-participation-confirmation-sub-title')
          }
          acceptLabel={modalTitle}
          danger
        />
        {freeLoginModal}
      </>
    );
  }
);
